import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import { useSupportData } from './SupportOverview';
import SupportInstanceSelector from '../../components/SupportInstanceSelector';
import { useSupportInstanceSelection } from '../../context/SupportInstanceSelectionContext';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import MetricCard from '../../components/MetricCard';
import MobileMetricSlider from '../../components/MobileMetricSlider';
import StaticAiPanel from '../../components/StaticAiPanel';
import SlidingAiPanel from '../../components/SlidingAiPanel';
import ProductLegend from '../../components/ProductLegend';
import { AccountDetails, SupportDashboardResponse } from '../../types/SupportTypes';
import { getSupportCustomerAiSummary } from '../../api';
import type { ChartOptions, ChartData } from 'chart.js';
import '../../styles/dashboard.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Radar, Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import LoadingSpinner from '../../components/LoadingSpinner';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
);

const SupportCustomers: React.FC = () => {
  const { data, instances, loading, error } = useSupportData();
  const { selectedSupportCustomerIntegrationId, setSelectedSupportCustomerIntegrationId } = useSupportInstanceSelection();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobile(width < 768);
      
      // Close mobile drawer when switching to desktop
      if (width >= 768) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const shouldStack = windowWidth < 768;
  const location = useLocation();
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [customerData, setCustomerData] = useState<AccountDetails | null>(null);
  const [csatData, setCsatData] = useState<any>(null);
  const [caseRatesData, setCaseRatesData] = useState<any>(null);
  const [resolutionTimeData, setResolutionTimeData] = useState<any>(null);
  const [responseTimeData, setResponseTimeData] = useState<any>(null);
  const [productCasesData, setProductCasesData] = useState<any>(null);
  const [riskPercentage, setRiskPercentage] = useState<number>(0);
  const [visibleCustomers, setVisibleCustomers] = useState<Record<string, boolean>>({});
  const [aiSummary, setAiSummary] = useState<{ summary: string; createdOn?: string } | null>(null);
  const metricCardColorGood = '#f4ffed'; //green
  const metricCardColorBad = '#fad6d4'; //red
  const metricCardColorNeutral = '#fce8ac'; //white
  const metricCardValueColorGood = '#43a047'; //green
  const metricCardValueColorBad = '#e53935'; //red
  const metricCardValueColorNeutral = '#877814'; //yellow

  // Handle navigation state - set selectedCustomer if passed from navigation
  useEffect(() => {
    const state = location.state as { selectedCustomer?: string } | null;
    if (state?.selectedCustomer) {
      setSelectedCustomer(state.selectedCustomer);
      // Clear the state to prevent re-selection on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Process customers with risk scores for sorting and filtering
  const sortedCustomers = useMemo(() => {
    if (!data || !data.casesByAccount) return [];

    const customers = Object.keys(data.casesByAccount).map(name => {
      const customerData = data.casesByAccount[name];
      //const riskScore = calculateRiskPercentage(customerData);

      const riskScore = data?.customerRiskPercentages?.[name] || 0;
      return {
        name,
        riskScore,
        openCases: customerData.openCases
      };
    });

    // Sort by risk score (highest first)
    const sorted = customers.sort((a, b) => b.riskScore - a.riskScore);

    // Filter by search term if provided
    if (!searchTerm) return sorted;
    return sorted.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);



  // Process data when selected customer changes
  useEffect(() => {
    if (!data || !selectedCustomer) return;
    const customer = data.casesByAccount[selectedCustomer];
    if (customer) {
      setCustomerData(customer);
      setRiskPercentage(data?.customerRiskPercentages?.[selectedCustomer] || 0);

      // Process customer-specific data for charts
      setCsatData(processCsatData(selectedCustomer));
      setCaseRatesData(processCaseRatesData(selectedCustomer));
      setResolutionTimeData(processResolutionTimeData(selectedCustomer));
      setResponseTimeData(processResponseTimeData(selectedCustomer));
      setProductCasesData(processProductCasesData(selectedCustomer));
    }
  }, [data, selectedCustomer]);

  // Set default visibility for customers on page load
  useEffect(() => {
    if (data?.accountMonthlyTrendStats) {
      const customers = Object.keys(data.accountMonthlyTrendStats).sort();

      // For customers visibility: if more than 5 customers, show only top 3 by total assigned cases
      let customersVisibility: Record<string, boolean> = {};
      if (customers.length > 5) {
        // Calculate sum of assigned cases for each customer
        const customerCasesTotals = customers.map(customer => {
          const monthlyData = data.accountMonthlyTrendStats[customer];
          const totalAssignedCases = Object.values(monthlyData).reduce((sum: number, monthData: any) => sum + (monthData.assignedCases || 0), 0);
          return { customer, totalAssignedCases };
        });

        // Sort by total assigned cases (descending) and take top 3
        const top3Customers = customerCasesTotals
          .sort((a, b) => b.totalAssignedCases - a.totalAssignedCases)
          .slice(0, 3)
          .map(item => item.customer);

        // Set visibility - only top 3 visible
        customers.forEach(customer => {
          customersVisibility[customer] = top3Customers.includes(customer);
        });
      } else {
        // Show all customers if 5 or fewer
        customers.forEach(customer => {
          customersVisibility[customer] = true;
        });
      }

      setVisibleCustomers(customersVisibility);
    }
  }, [data]);

  // Handle body scroll prevention when mobile drawer is open
  useEffect(() => {
    if (isMobile) {
      if (mobileDrawerOpen) {
        // Store current scroll position
        const scrollY = window.scrollY;
        document.body.style.top = `-${scrollY}px`;
        document.body.classList.add('mobile-drawer-open');
      } else {
        // Restore scroll position
        const scrollY = document.body.style.top;
        document.body.classList.remove('mobile-drawer-open');
        document.body.style.top = '';
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-drawer-open');
      document.body.style.top = '';
    };
  }, [mobileDrawerOpen, isMobile]);

  // Update AI summary when selectedCustomer changes
  useEffect(() => {
    if (data) {
      if (selectedCustomer) {
        // Clear the AI summary when a customer is selected
        setAiSummary(null);
      } else {
        // Use overview AI summary from the dashboard response when no customer is selected
        setAiSummary(data.aiAccountSummary ? {
          summary: data.aiAccountSummary.summary,
          createdOn: data.aiAccountSummary.createdOn
        } : null);
      }
    }
  }, [selectedCustomer, data]);

  // Callback to update AI summary when streaming content changes
  const updateAiSummary = (summary: string, createdOn: string) => {
    setAiSummary({ summary, createdOn });
  };

  // Function to fetch customer AI summary
  const fetchCustomerSummary = async () => {
    try {
      if (selectedCustomer && data) {
        const customerSummary = await getSupportCustomerAiSummary(selectedCustomer, data.summaryId);
        return customerSummary || { summary: '', createdOn: '' };
      }
      return { summary: '', createdOn: '' };
    } catch (error) {
      console.error('Error fetching customer AI summary:', error);
      return { summary: 'Failed to load customer insights. Please try again later.', createdOn: '' };
    }
  };

  // Helper function to format time in minutes to a readable format
  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    } else if (minutes < 1440) {
      return `${Math.round(minutes / 60 * 10) / 10} hrs`;
    } else {
      return `${Math.round(minutes / 1440 * 10) / 10} days`;
    }
  };

  // Get color based on risk percentage
  const getRiskColor = (riskScore: number, isForValue: boolean): string => {
    if (isForValue) {
      if (riskScore >= 70) return metricCardValueColorBad;
      if (riskScore >= 50) return metricCardValueColorNeutral;
      return metricCardValueColorGood;
    }
    else {
      if (riskScore >= 70) return metricCardColorBad;
      if (riskScore >= 50) return metricCardColorNeutral;
      return metricCardColorGood;
    }
  };

  // Handle customer selection from the dialog
  const handleCustomerSelect = (customerName: string) => {
    setSelectedCustomer(customerName);
    setIsDialogOpen(false);
  };

  // Process CSAT data for the selected customer
  const processCsatData = (customer: string) => {
    // Check if we have customer-specific CSAT data
    if (data?.csatMonthlyAverageByCustomer) {
      // Get all months that have data for this customer
      const customerCsatMonths = new Set<string>();
      Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
        if (data.csatMonthlyAverageByCustomer?.[month]?.[customer]) {
          customerCsatMonths.add(month);
        }
      });

      if (customerCsatMonths.size > 0) {
        // We have customer-specific data
        const months = Array.from(customerCsatMonths).sort();
        const formattedMonths = months.map(month => {
          if (month.includes('-')) {
            const [year, monthNum] = month.split('-');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
          }
          return month;
        });

        const customerCsatScores = months.map(month => {
          return data.csatMonthlyAverageByCustomer?.[month]?.[customer]?.averageNormalizedScore || 0;
        });

        return {
          labels: formattedMonths,
          datasets: [
            {
              label: `${customer} CSAT Score`,
              data: customerCsatScores,
              borderColor: '#1976d2',
              backgroundColor: '#1976d220',
              tension: 0.1,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6,
            }
          ]
        };
      }
    }

    // Check if we have customer-specific CSAT data
    if (data?.csatMonthlyAverageByCustomer) {
      // Get all months that have data for this customer
      const customerCsatMonths = new Set<string>();
      Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
        if (data.csatMonthlyAverageByCustomer?.[month]?.[customer]) {
          customerCsatMonths.add(month);
        }
      });

      if (customerCsatMonths.size > 0) {
        // We have customer-specific data
        const months = Array.from(customerCsatMonths).sort();
        const formattedMonths = months.map(month => {
          if (month.includes('-')) {
            const [year, monthNum] = month.split('-');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
          }
          return month;
        });

        const customerCsatScores = months.map(month => {
          return data.csatMonthlyAverageByCustomer?.[month]?.[customer]?.averageNormalizedScore || 0;
        });

        return {
          labels: formattedMonths,
          datasets: [
            {
              label: `${customer} CSAT Score`,
              data: customerCsatScores,
              borderColor: '#1976d2',
              backgroundColor: '#1976d220',
              tension: 0.1,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6,
            }
          ]
        };
      }
    }

    // Return null if no customer-specific data available
    return null;
  };

  // Process case rates data for the selected customer
  const processCaseRatesData = (customer: string) => {
    if (!data?.accountMonthlyTrendStats) return null;

    // Extract customer-specific data from AccountMonthlyTrendStats
    const customerMonthlyData: { [month: string]: { assignedCases: number, resolvedCases: number } } = {};

    // Check if we have data for this customer
    Object.keys(data.accountMonthlyTrendStats).forEach(account => {
      if (account === customer) {
        // Get data for each month for this customer
        Object.keys(data.accountMonthlyTrendStats[account]).forEach(month => {
          const monthData = data.accountMonthlyTrendStats[account][month];
          customerMonthlyData[month] = {
            assignedCases: monthData.assignedCases || 0,
            resolvedCases: monthData.resolvedCases || 0
          };
        });
      }
    });

    // If no customer-specific data found, return null
    if (Object.keys(customerMonthlyData).length === 0) return null;

    // Sort months chronologically
    const months = Object.keys(customerMonthlyData).sort();

    // Format months for display
    const formattedMonths = months.map(month => {
      if (month.includes('-')) {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      }
      return month;
    });

    // Extract assigned (opened) and resolved (closed) cases for each month and make them cumulative
    let cumulativeAssigned = 0;
    let cumulativeResolved = 0;

    const assignedCases = months.map(month => {
      cumulativeAssigned += customerMonthlyData[month].assignedCases;
      return cumulativeAssigned;
    });

    const resolvedCases = months.map(month => {
      cumulativeResolved += customerMonthlyData[month].resolvedCases;
      return cumulativeResolved;
    });

    return {
      labels: formattedMonths,
      datasets: [
        {
          label: 'Assigned Cases',
          data: assignedCases,
          borderColor: '#e53935', // red
          backgroundColor: '#e5393520',
          tension: 0.1,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Resolved Cases',
          data: resolvedCases,
          borderColor: '#43a047', // green
          backgroundColor: '#43a04720',
          tension: 0.1,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ]
    };
  };

  // Process resolution time data for the selected customer
  const processResolutionTimeData = (customer: string) => {
    if (!data?.accountMonthlyTrendStats) return null;

    // Get customer-specific monthly data
    const customerMonthlyData = data.accountMonthlyTrendStats[customer];
    if (!customerMonthlyData) return null;

    // Get available months for this customer and sort them
    const months = Object.keys(customerMonthlyData).sort();
    if (months.length === 0) return null;

    const formattedMonths = months.map(month => {
      if (month.includes('-')) {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      }
      return month;
    });

    // Extract customer-specific resolution times for each month
    const resolutionTimes = months.map(month => {
      const monthData = customerMonthlyData[month];
      const timeInMinutes = monthData?.avgResolutionTime || 0;
      // Convert from minutes to hours
      return timeInMinutes / 60;
    });

    return {
      labels: formattedMonths,
      datasets: [
        {
          label: 'Avg Resolution Time (hours)',
          data: resolutionTimes,
          borderColor: '#ff9800',
          backgroundColor: '#ff980020',
          tension: 0.1,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ]
    };
  };

  // Process first response time data for the selected customer
  const processResponseTimeData = (customer: string) => {
    if (!data?.accountMonthlyTrendStats) return null;

    // Get customer-specific monthly data
    const customerMonthlyData = data.accountMonthlyTrendStats[customer];
    if (!customerMonthlyData) return null;

    // Get available months for this customer and sort them
    const months = Object.keys(customerMonthlyData).sort();
    if (months.length === 0) return null;

    const formattedMonths = months.map(month => {
      if (month.includes('-')) {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      }
      return month;
    });

    // Extract customer-specific first response times for each month
    const responseTimes = months.map(month => {
      const monthData = customerMonthlyData[month];
      const timeInMinutes = monthData?.avgFirstResponseTime || 0;
      // Convert from minutes to hours
      return timeInMinutes / 60;
    });

    return {
      labels: formattedMonths,
      datasets: [
        {
          label: 'Avg First Response Time (hours)',
          data: responseTimes,
          borderColor: '#9c27b0',
          backgroundColor: '#9c27b020',
          tension: 0.1,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  // Process product cases data for the selected customer
  const processProductCasesData = (customer: string) => {
    if (!data?.casesByCategory) return null;

    // Get customer-specific data if available
    const customerData = data.casesByAccount?.[customer];

    // Use customer-specific product distribution if available, otherwise use overall
    // For now, we'll use the overall category data as a proxy for product data
    const products = Object.keys(data.casesByCategory).sort();
    const scaleFactor = customerData ? (customerData.count / (data.casesTotal || 1)) : 1;
    const caseCounts = products.map(product =>
      Math.round((data.casesByCategory[product] || 0) * scaleFactor)
    );

    return {
      labels: products,
      datasets: [
        {
          label: 'Cases',
          data: caseCounts,
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Process customer-specific priority status data for stacked bar chart
  const processCustomerPriorityStatusData = (customer: string) => {
    if (!data?.customerCasesByStatus) return null;

    // Get customer's monthly priority/status breakdown from the new customerCasesByStatus data
    const customerMonthlyData = data.customerCasesByStatus[customer];
    if (!customerMonthlyData) return null;

    // Get the latest month's data (most recent month with data)
    const availableMonths = Object.keys(customerMonthlyData).sort();
    if (availableMonths.length === 0) return null;

    const latestMonth = availableMonths[availableMonths.length - 1];
    const customerPriorityData = customerMonthlyData[latestMonth];
    if (!customerPriorityData) return null;

    // Get all unique statuses across all priorities for this customer
    const allStatuses = new Set<string>();
    Object.values(customerPriorityData).forEach(priorityData => {
      Object.keys(priorityData).forEach(status => allStatuses.add(status));
    });

    // Sort priorities by importance (critical > high > medium > low)
    const priorityOrder = ['critical', 'high', 'medium', 'low'];
    const priorities = Object.keys(customerPriorityData).sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.toLowerCase());
      const indexB = priorityOrder.indexOf(b.toLowerCase());
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    // Generate color for each status
    const generateColor = (index: number): string => {
      const colors = [
        '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
        '#bcbd22', '#17becf', '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5', '#c49c94',
        '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5'
      ];
      return colors[index % colors.length];
    };

    // Generate datasets - one dataset per status
    const datasets = Array.from(allStatuses).map((status, index) => {
      const color = generateColor(index);
      return {
        label: status.replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&'),
        data: priorities.map(priority => {
          const priorityData = customerPriorityData[priority];
          return (priorityData && typeof priorityData === 'object' && priorityData[status]) || 0;
        }),
        backgroundColor: color,
      };
    });

    //decode all priorities
    const decodedPriorities = priorities.map(priority => priority.replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&'));

    return {
      labels: decodedPriorities,
      datasets,
      latestMonth // Include the month being displayed for reference
    };
  };

  // Process CSAT extremes data (highest and lowest scoring customers per month)
  const processCsatExtremesData = () => {
    if (!data?.csatMonthlyAverageByCustomer) return null;

    const monthlyExtremes: Array<{
      month: string;
      formattedMonth: string;
      highest: { customer: string; score: number; responses: number } | null;
      lowest: { customer: string; score: number; responses: number } | null;
    }> = [];

    // Process each month (most recent first)
    Object.keys(data.csatMonthlyAverageByCustomer).sort().reverse().forEach(month => {
      const monthData = data.csatMonthlyAverageByCustomer![month];

      let highest: { customer: string; score: number; responses: number } | null = null;
      let lowest: { customer: string; score: number; responses: number } | null = null;

      // Find highest and lowest scoring customers for this month
      Object.keys(monthData).forEach(customer => {
        const customerData = monthData[customer] as any;
        if (customerData && typeof customerData.averageNormalizedScore === 'number' && customerData.totalResponses > 0) {
          const score = customerData.averageNormalizedScore;
          const responses = customerData.totalResponses;

          if (!highest || score > highest.score) {
            highest = { customer, score, responses };
          }
          if (!lowest || score < lowest.score) {
            lowest = { customer, score, responses };
          }
        }
      });

      // Format month for display
      const formattedMonth = month.includes('-') ? (() => {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      })() : month;

      if (highest || lowest) {
        monthlyExtremes.push({
          month,
          formattedMonth,
          highest,
          lowest
        });
      }
    });

    return monthlyExtremes;
  };

  // Process customer-specific backlog aging data
  const processCustomerBacklogAgingData = (customer: string) => {
    if (!data?.customerBacklogAgingProfile || !customerData) return null;

    // Get the specific customer's aging data from the new customerBacklogAgingProfile property
    const customerAgingData = data.customerBacklogAgingProfile[customer];

    if (!customerAgingData) return null;

    // Define the expected order of age ranges
    const ageRangeOrder = ['0-7 days', '8-14 days', '15-30 days', '31-60 days', '>60 days'];

    // Sort labels according to the expected order
    const sortedLabels = Object.keys(customerAgingData).sort((a, b) => {
      return ageRangeOrder.indexOf(a) - ageRangeOrder.indexOf(b);
    });

    // Use the actual customer-specific values
    const values = sortedLabels.map(label => customerAgingData[label] || 0);

    // Define color scale from green to red
    const colorScale: Record<string, string> = {
      '0-7 days': '#4caf50',     // Green
      '8-14 days': '#8bc34a',    // Light Green
      '15-30 days': '#ffeb3b',   // Yellow
      '31-60 days': '#ff9800',   // Orange
      '>60 days': '#f44336'      // Red
    };

    // Map colors to each age range
    const backgroundColors = sortedLabels.map(label => colorScale[label] || '#4bc0c0');
    const borderColors = backgroundColors.map(color =>
      color.replace('rgb', 'rgba').replace(')', ', 0.8)')
    );

    //decode all sortedlabels
    const decodedLabels = sortedLabels.map(label => label.replace(/&gt;/g, '>').replace(/&lt;/g, '<'));

    return {
      labels: decodedLabels,
      datasets: [
        {
          label: 'Number of Open Cases',
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.7,
        },
      ],
    };
  };

  // Helper function to format CSAT score (0 to 1 scale to percentage)
  const formatCsatScore = (score?: number): string => {
    return score ? `${(score * 100).toFixed(1)}%` : 'N/A';
  };

  // Calculate customer-specific high priority open cases
  const calculateCustomerHighPriorityOpenCases = () => {
    if (!selectedCustomer || !data?.accountMonthlyTrendStats) return { count: 0, percent: 0 };

    let highPriorityOpenCount = 0;
    let customerTotalCreatedCases = 0;

    // Get customer data from accountMonthlyTrendStats
    const customerData = data.accountMonthlyTrendStats[selectedCustomer];

    if (customerData) {
      // Get the latest month since cumulativeCreatedCasesByPriority is cumulative
      const months = Object.keys(customerData).sort();
      const latestMonth = months[months.length - 1];

      if (latestMonth) {
        const monthData = customerData[latestMonth];

        if (monthData?.cumulativeCreatedCasesByPriority) {
          const openCasesByPriority = monthData.cumulativeCreatedCasesByPriority;

          // Count high and critical priority cases (case-insensitive)
          Object.keys(openCasesByPriority).forEach(priority => {
            const priorityLower = priority.toLowerCase();
            const count = openCasesByPriority[priority] || 0;

            // Count total created cases for percentage calculation
            customerTotalCreatedCases += count;

            // Count high priority cases (critical and high)
            if (priorityLower === 'critical' || priorityLower === 'high') {
              highPriorityOpenCount += count;
            }
          });
        }
      }
    }

    // Calculate percentage of customer's open cases
    const percent = customerTotalCreatedCases > 0 ? (highPriorityOpenCount / customerTotalCreatedCases) * 100 : 0;

    return {
      count: highPriorityOpenCount,
      percent
    };
  };

  // Get latest month's high priority cases for backValue
  const getLatestMonthHighPriorityCases = () => {
    if (!selectedCustomer || !data?.accountMonthlyTrendStats) return 0;

    const customerData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerData) return 0;

    const months = Object.keys(customerData).sort();
    if (months.length === 0) return 0;

    const latestMonth = months[months.length - 1];
    const monthData = customerData[latestMonth];

    if (!monthData?.monthlyCreatedCasesByPriority) return 0;

    let highPriorityCount = 0;
    Object.keys(monthData.monthlyCreatedCasesByPriority || {}).forEach(priority => {
      const priorityLower = priority.toLowerCase();
      if (priorityLower === 'critical' || priorityLower === 'high') {
        highPriorityCount += (monthData.monthlyCreatedCasesByPriority?.[priority]) || 0;
      }
    });

    return highPriorityCount;
  };

  // Get high priority trend data for calculations
  const getHighPriorityTrend = () => {
    if (!selectedCustomer || !data?.accountMonthlyTrendStats) return null;

    const customerData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerData) return null;

    const months = Object.keys(customerData).sort();
    if (months.length < 2) return null;

    const latestMonth = months[months.length - 1];
    const previousMonth = months[months.length - 2];

    const latestData = customerData[latestMonth];
    const previousData = customerData[previousMonth];

    if (!latestData?.monthlyCreatedCasesByPriority || !previousData?.monthlyCreatedCasesByPriority) {
      return null;
    }

    // Calculate high priority cases for latest month
    let latestHighPriority = 0;
    Object.keys(latestData.monthlyCreatedCasesByPriority || {}).forEach(priority => {
      const priorityLower = priority.toLowerCase();
      if (priorityLower === 'critical' || priorityLower === 'high') {
        latestHighPriority += (latestData.monthlyCreatedCasesByPriority?.[priority]) || 0;
      }
    });

    // Calculate high priority cases for previous month
    let previousHighPriority = 0;
    Object.keys(previousData.monthlyCreatedCasesByPriority || {}).forEach(priority => {
      const priorityLower = priority.toLowerCase();
      if (priorityLower === 'critical' || priorityLower === 'high') {
        previousHighPriority += (previousData.monthlyCreatedCasesByPriority?.[priority]) || 0;
      }
    });

    if (previousHighPriority === 0) {
      return {
        difference: latestHighPriority,
        percentChange: latestHighPriority > 0 ? 100 : 0,
        hasData: true
      };
    }

    const difference = latestHighPriority - previousHighPriority;
    const percentChange = (difference / previousHighPriority) * 100;

    return {
      difference,
      percentChange,
      hasData: true
    };
  };

  // Format high priority trend description
  const getHighPriorityTrendDescription = () => {
    const trendData = getHighPriorityTrend();

    if (!trendData || !trendData.hasData) {
      return "No trend data available";
    }

    const { difference, percentChange } = trendData;
    const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

    return `${trend} ${Math.abs(difference)} cases (${percentChange.toFixed(1)}%) vs previous month`;
  };

  // Get color based on high priority trend
  const getHighPriorityTrendColor = (colorType: 'card' | 'value') => {
    const trendData = getHighPriorityTrend();

    if (!trendData || !trendData.hasData) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    }

    const { percentChange, difference } = trendData;

    // If percentage difference < -30: good
    // If percentage difference > 10: bad
    // Else: neutral
    if (percentChange < -30) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    } else if (percentChange > 10) {
      return colorType === 'card' ? metricCardColorBad : metricCardValueColorBad;
    } else {
      if (difference === 0) {
        return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
      } else {
        return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
      }
    }
  };

  // Get CSAT trend data for calculations
  const getCsatTrend = () => {
    if (!selectedCustomer || !data?.csatMonthlyAverageByCustomer) return null;

    // Get all months with customer data and sort them
    const customerMonths = Object.keys(data.csatMonthlyAverageByCustomer)
      .filter(month => data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer])
      .sort();

    if (customerMonths.length < 2) return null;

    // Get current and previous month data
    const currentMonth = customerMonths[customerMonths.length - 1];
    const previousMonth = customerMonths[customerMonths.length - 2];

    const currentData = data.csatMonthlyAverageByCustomer[currentMonth][selectedCustomer];
    const previousData = data.csatMonthlyAverageByCustomer[previousMonth][selectedCustomer];

    if (!currentData || !previousData ||
      typeof currentData.averageNormalizedScore !== 'number' ||
      typeof previousData.averageNormalizedScore !== 'number') {
      return null;
    }

    const currentScore = currentData.averageNormalizedScore;
    const previousScore = previousData.averageNormalizedScore;
    const percentChange = ((currentScore - previousScore) / previousScore) * 100;
    const difference = currentScore - previousScore;

    return {
      difference,
      percentChange,
      hasData: true,
      currentResponses: currentData.totalResponses || 0
    };
  };

  // Format CSAT trend description
  const getCsatTrendDescription = () => {
    const trendData = getCsatTrend();

    if (!trendData || !trendData.hasData) {
      if (!selectedCustomer || !data?.csatMonthlyAverageByCustomer) {
        return 'No CSAT trend data available';
      }

      const customerMonths = Object.keys(data.csatMonthlyAverageByCustomer)
        .filter(month => data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer])
        .sort();

      if (customerMonths.length === 1) {
        const currentMonth = customerMonths[0];
        const currentData = data.csatMonthlyAverageByCustomer[currentMonth][selectedCustomer];
        const responses = currentData?.totalResponses || 0;
        return `Current month (${responses} responses)`;
      }

      return 'No CSAT trend data available';
    }

    const { percentChange, currentResponses } = trendData;
    const direction = percentChange >= 0 ? '↑' : '↓';
    const sign = percentChange >= 0 ? '+' : '';

    return `${direction} ${sign}${percentChange.toFixed(1)}% vs previous month (${currentResponses} responses)`;
  };

  // Get color based on CSAT trend
  const getCsatTrendColor = (colorType: 'card' | 'value') => {
    const trendData = getCsatTrend();

    if (!trendData || !trendData.hasData) {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }

    const { percentChange } = trendData;

    // For CSAT: higher is better, lower is worse
    // If percentage difference > 10: good (CSAT improving significantly)
    // If percentage difference < -30: bad (CSAT declining significantly)
    // Else: neutral
    if (percentChange > 10) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    } else if (percentChange < -30) {
      return colorType === 'card' ? metricCardColorBad : metricCardValueColorBad;
    } else {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }
  };

  // Get SLA breaches trend data for calculations
  const getSlaBreachesTrend = () => {
    if (!selectedCustomer || !data?.monthlySlaBreachesByCustomer) return null;

    // Get all months with customer breach data and sort them
    const customerMonths = Object.keys(data.monthlySlaBreachesByCustomer)
      .filter(month => data.monthlySlaBreachesByCustomer?.[month]?.[selectedCustomer])
      .sort();

    if (customerMonths.length < 2) return null;

    // Get current and previous month data
    const currentMonth = customerMonths[customerMonths.length - 1];
    const previousMonth = customerMonths[customerMonths.length - 2];

    const currentData = data.monthlySlaBreachesByCustomer[currentMonth][selectedCustomer];
    const previousData = data.monthlySlaBreachesByCustomer[previousMonth][selectedCustomer];

    if (!currentData || !previousData) {
      return null;
    }

    const currentBreachRate = currentData.breachRatePercentage || 0;
    const previousBreachRate = previousData.breachRatePercentage || 0;

    // Calculate percentage change in breach rate
    const difference = currentBreachRate - previousBreachRate;
    const percentChange = previousBreachRate > 0 ? (difference / previousBreachRate) * 100 : (currentBreachRate > 0 ? 100 : 0);

    return {
      difference,
      percentChange,
      hasData: true,
      currentBreachCount: currentData.breachCount || 0,
      currentTotalCases: currentData.totalCases || 0,
      currentBreachRate
    };
  };

  // Format SLA breaches trend description
  const getSlaBreachesTrendDescription = () => {
    const trendData = getSlaBreachesTrend();

    if (!trendData || !trendData.hasData) {
      if (!selectedCustomer || !data?.monthlySlaBreachesByCustomer) {
        return 'No SLA breaches recorded';
      }

      const customerMonths = Object.keys(data.monthlySlaBreachesByCustomer)
        .filter(month => data.monthlySlaBreachesByCustomer?.[month]?.[selectedCustomer])
        .sort();

      if (customerMonths.length === 1) {
        const currentMonth = customerMonths[0];
        const currentData = data.monthlySlaBreachesByCustomer[currentMonth][selectedCustomer];
        const breachCount = currentData?.breachCount || 0;
        //const breachRate = currentData?.breachRatePercentage || 0;
        return `${breachCount} new breach${breachCount === 1 ? '' : 'es'} this month`;
      }

      return 'No SLA breaches recorded';
    }

    const { percentChange, currentBreachCount, currentBreachRate } = trendData;
    const direction = percentChange >= 0 ? '↑' : '↓';
    const sign = percentChange >= 0 ? '+' : '';

    return `${direction} ${sign}${percentChange.toFixed(1)}% vs previous month (${currentBreachCount} breaches, ${currentBreachRate.toFixed(1)}%)`;
  };

  // Get color based on SLA breaches trend
  const getSlaBreachesTrendColor = (colorType: 'card' | 'value') => {
    const trendData = getSlaBreachesTrend();

    if (!trendData || !trendData.hasData) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    }

    const { percentChange, difference } = trendData;

    // For SLA breaches: lower is better, higher is worse
    // If percentage difference < -30: good (breach rate improving significantly)
    // If percentage difference > 10: bad (breach rate worsening significantly)
    // Else: neutral
    if (percentChange < -30) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    } else if (percentChange > 10) {
      return colorType === 'card' ? metricCardColorBad : metricCardValueColorBad;
    } else {
      if (difference === 0) {
        return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
      } else {
        return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
      }
    }
  };

  // Get opened cases trend data for calculations
  const getOpenedCasesTrend = () => {
    if (!selectedCustomer || !data?.accountMonthlyTrendStats) return null;

    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerMonthlyData) return null;

    const months = Object.keys(customerMonthlyData).sort();
    if (months.length < 2) return null;

    const latestMonth = months[months.length - 1];
    const previousMonth = months[months.length - 2];

    const latestData = customerMonthlyData[latestMonth];
    const previousData = customerMonthlyData[previousMonth];

    if (!latestData || !previousData) return null;

    const currentCases = latestData.assignedCases || 0;
    const previousCases = previousData.assignedCases || 0;

    const difference = currentCases - previousCases;
    const percentChange = previousCases > 0 ? (difference / previousCases) * 100 : (currentCases > 0 ? 100 : 0);

    return {
      difference,
      percentChange,
      hasData: true,
      currentCases,
      previousCases
    };
  };

  // Format opened cases trend description
  const getOpenedCasesTrendDescription = () => {
    const trendData = getOpenedCasesTrend();

    if (!trendData || !trendData.hasData) {
      if (!selectedCustomer || !data?.accountMonthlyTrendStats) {
        return 'No opened cases trend data available';
      }

      const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
      if (!customerMonthlyData) return 'No data for this customer';

      const months = Object.keys(customerMonthlyData).sort();
      if (months.length === 1) {
        const currentMonth = months[0];
        const currentData = customerMonthlyData[currentMonth];
        const cases = currentData?.assignedCases || 0;
        return `${cases} new case${cases === 1 ? '' : 's'} this month`;
      }

      return 'No opened cases trend data available';
    }

    const { difference, percentChange } = trendData;
    const direction = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';
    const sign = difference > 0 ? '+' : '';

    return `${direction} ${sign}${Math.abs(difference)} case${Math.abs(difference) === 1 ? '' : 's'} (${sign}${percentChange.toFixed(1)}%) vs previous month`;
  };

  // Get color based on opened cases trend
  const getOpenedCasesTrendColor = (colorType: 'card' | 'value') => {
    const trendData = getOpenedCasesTrend();

    if (!trendData || !trendData.hasData) {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }

    const { percentChange, difference } = trendData;

    // For opened cases: lower is better, higher is worse (more cases = more work)
    // If percentage difference < -30: good (fewer cases opened)
    // If percentage difference > 10: bad (more cases opened)
    // Else: neutral
    if (percentChange < -30) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    } else if (percentChange > 10) {
      return colorType === 'card' ? metricCardColorBad : metricCardValueColorBad;
    } else {
      if (difference === 0) {
        return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
      } else {
        return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
      }
    }
  };

  // Get first response time trend data for calculations
  const getFirstResponseTimeTrend = () => {
    if (!selectedCustomer || !data?.accountMonthlyTrendStats) return null;

    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerMonthlyData) return null;

    const months = Object.keys(customerMonthlyData).sort();
    if (months.length < 2) return null;

    const latestMonth = months[months.length - 1];
    const previousMonth = months[months.length - 2];

    const latestData = customerMonthlyData[latestMonth];
    const previousData = customerMonthlyData[previousMonth];

    if (!latestData || !previousData) return null;

    const currentTime = latestData.avgFirstResponseTime || 0;
    const previousTime = previousData.avgFirstResponseTime || 0;

    const difference = currentTime - previousTime;
    const percentChange = previousTime > 0 ? (difference / previousTime) * 100 : (currentTime > 0 ? 100 : 0);

    return {
      difference,
      percentChange,
      hasData: true,
      currentTime,
      previousTime
    };
  };

  // Format first response time trend description
  const getFirstResponseTimeTrendDescription = () => {
    const trendData = getFirstResponseTimeTrend();

    if (!trendData || !trendData.hasData) {
      if (!selectedCustomer || !data?.accountMonthlyTrendStats) {
        return 'No first response time trend data available';
      }

      const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
      if (!customerMonthlyData) return 'No data for this customer';

      const months = Object.keys(customerMonthlyData).sort();
      if (months.length === 1) {
        const currentMonth = months[0];
        const currentData = customerMonthlyData[currentMonth];
        const time = currentData?.avgFirstResponseTime || 0;
        return `Current month: ${formatTime(time)}`;
      }

      return 'No first response time trend data available';
    }

    const { difference, percentChange } = trendData;
    const direction = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';
    const sign = difference > 0 ? '+' : '';

    return `${direction} ${sign}${formatTime(Math.abs(difference))} (${sign}${percentChange.toFixed(1)}%) vs previous month`;
  };

  // Get color based on first response time trend
  const getFirstResponseTimeTrendColor = (colorType: 'card' | 'value') => {
    const trendData = getFirstResponseTimeTrend();

    if (!trendData || !trendData.hasData) {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }

    const { percentChange, difference } = trendData;

    // For first response time: lower is better, higher is worse (faster response = better)
    // If percentage difference < -30: good (response time improving significantly)
    // If percentage difference > 10: bad (response time worsening significantly)
    // Else: neutral
    if (percentChange < -30) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    } else if (percentChange > 10) {
      return colorType === 'card' ? metricCardColorBad : metricCardValueColorBad;
    } else {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }
  };

  // Get resolution time trend data for calculations
  const getResolutionTimeTrend = () => {
    if (!selectedCustomer || !data?.accountMonthlyTrendStats) return null;

    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerMonthlyData) return null;

    const months = Object.keys(customerMonthlyData).sort();
    if (months.length < 2) return null;

    const latestMonth = months[months.length - 1];
    const previousMonth = months[months.length - 2];

    const latestData = customerMonthlyData[latestMonth];
    const previousData = customerMonthlyData[previousMonth];

    if (!latestData || !previousData) return null;

    const currentTime = latestData.avgResolutionTime || 0;
    const previousTime = previousData.avgResolutionTime || 0;

    const difference = currentTime - previousTime;
    const percentChange = previousTime > 0 ? (difference / previousTime) * 100 : (currentTime > 0 ? 100 : 0);

    return {
      difference,
      percentChange,
      hasData: true,
      currentTime,
      previousTime
    };
  };

  // Format resolution time trend description
  const getResolutionTimeTrendDescription = () => {
    const trendData = getResolutionTimeTrend();

    if (!trendData || !trendData.hasData) {
      if (!selectedCustomer || !data?.accountMonthlyTrendStats) {
        return 'No resolution time trend data available';
      }

      const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
      if (!customerMonthlyData) return 'No data for this customer';

      const months = Object.keys(customerMonthlyData).sort();
      if (months.length === 1) {
        const currentMonth = months[0];
        const currentData = customerMonthlyData[currentMonth];
        const time = currentData?.avgResolutionTime || 0;
        return `Current month: ${formatTime(time)}`;
      }

      return 'No resolution time trend data available';
    }

    const { difference, percentChange } = trendData;
    const direction = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';
    const sign = difference > 0 ? '+' : '';

    return `${direction} ${sign}${formatTime(Math.abs(difference))} (${sign}${percentChange.toFixed(1)}%) vs previous month`;
  };

  // Get color based on resolution time trend
  const getResolutionTimeTrendColor = (colorType: 'card' | 'value') => {
    const trendData = getResolutionTimeTrend();

    if (!trendData || !trendData.hasData) {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }

    const { percentChange } = trendData;

    // For resolution time: lower is better, higher is worse (faster resolution = better)
    // If percentage difference < -30: good (resolution time improving significantly)
    // If percentage difference > 10: bad (resolution time worsening significantly)
    // Else: neutral
    if (percentChange < -30) {
      return colorType === 'card' ? metricCardColorGood : metricCardValueColorGood;
    } else if (percentChange > 10) {
      return colorType === 'card' ? metricCardColorBad : metricCardValueColorBad;
    } else {
      return colorType === 'card' ? metricCardColorNeutral : metricCardValueColorNeutral;
    }
  };

  // Calculate customer-specific SLA breaches
  const calculateCustomerSlaBreaches = () => {
    if (!data?.monthlySlaBreachesByCustomer || !customerData || !selectedCustomer) {
      return { count: 0, percent: 0 };
    }

    let totalBreaches = 0;
    let totalCases = 0;

    // Aggregate breach data across all months for this customer
    Object.values(data.monthlySlaBreachesByCustomer).forEach(monthData => {
      const customerBreachData = monthData[selectedCustomer];
      if (customerBreachData) {
        totalBreaches += customerBreachData.breachCount;
        totalCases += customerBreachData.totalCases;
      }
    });

    // Calculate percentage based on actual customer data
    const percent = totalCases > 0 ? (totalBreaches / totalCases) * 100 : 0;

    return {
      count: totalBreaches,
      percent
    };
  };

  // Get customer monthly opened cases (for trend data)
  const getCustomerMonthlyOpenedCases = () => {
    if (!data?.accountMonthlyTrendStats || !selectedCustomer) return 'No data';

    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerMonthlyData) return 'No data';

    const months = Object.keys(customerMonthlyData).sort();
    if (months.length === 0) return 'No data';

    const latestMonth = months[months.length - 1];
    const latestData = customerMonthlyData[latestMonth];

    return latestData?.assignedCases?.toString() || 'No data';
  };

  // Get customer monthly trend description
  const getCustomerMonthlyTrend = () => {
    if (!data?.accountMonthlyTrendStats || !selectedCustomer) return 'No trend data available';

    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
    if (!customerMonthlyData) return 'No data for this customer';

    const months = Object.keys(customerMonthlyData).sort();
    if (months.length < 2) return 'Insufficient data for trend';

    const latestMonth = months[months.length - 1];
    const previousMonth = months[months.length - 2];

    const latestData = customerMonthlyData[latestMonth];
    const previousData = customerMonthlyData[previousMonth];

    if (!latestData || !previousData) return 'Insufficient data for trend';

    const difference = (latestData.assignedCases || 0) - (previousData.assignedCases || 0);
    const percentChange = (previousData.assignedCases || 0) > 0 ?
      ((difference / (previousData.assignedCases || 1)) * 100).toFixed(1) : '0';
    const trend = difference > 0 ? '↑' : difference < 0 ? '↓' : '→';

    return `${trend} ${Math.abs(difference)} cases (${Math.abs(Number(percentChange))}%) vs previous month`;
  };

  // Generate color for customers
  const generateColor = (index: number): string => {
    const colors = [
      '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
      '#bcbd22', '#17becf', '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5', '#c49c94',
      '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5', '#1f78b4', '#33a02c', '#fb9a99', '#e31a1c',
      '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a', '#ffff99', '#b15928'
    ];
    return colors[index % colors.length];
  };

  // Process overall CSAT trend data
  const processOverallCsatTrendData = () => {
    if (!data?.csatMonthlyAverage) return null;
    const csatData = data.csatMonthlyAverage;

    const months = Object.keys(csatData).sort();
    const formattedMonths = months.map(month => {
      if (month.includes('-')) {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      }
      return month;
    });

    const csatScores = months.map(month => {
      return csatData[month]?.averageNormalizedScore || 0;
    });

    return {
      labels: formattedMonths,
      datasets: [
        {
          label: 'Average CSAT Score',
          data: csatScores,
          borderColor: '#3949ab',
          backgroundColor: '#3949ab20',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ]
    };
  };

  // Process customer monthly trend data for chart
  const processCustomerTrendData = () => {
    if (!data?.accountMonthlyTrendStats) return null;

    // Get all unique months and sort them
    const allMonths = new Set<string>();
    Object.values(data.accountMonthlyTrendStats).forEach(customerData => {
      Object.keys(customerData).forEach(month => allMonths.add(month));
    });

    const sortedMonths = Array.from(allMonths).sort();

    // Format months for display
    const formattedMonths = sortedMonths.map(month => {
      if (month.includes('-')) {
        const [year, monthNum] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
      }
      return month;
    });

    // Calculate total assigned cases per customer across all months
    const customerTotals = Object.keys(data.accountMonthlyTrendStats).map(customer => {
      const customerData = data.accountMonthlyTrendStats[customer];
      const totalAssignedCases = Object.values(customerData).reduce(
        (sum, monthData) => sum + (monthData.assignedCases || 0), 0
      );
      return { customer, totalAssignedCases };
    });

    // Sort customers by total assigned cases (descending)
    const sortedCustomers = customerTotals
      .sort((a, b) => b.totalAssignedCases - a.totalAssignedCases)
      .map(item => item.customer);

    // Create datasets for each customer
    const datasets = sortedCustomers.map((customer, index) => {
      const customerData = data.accountMonthlyTrendStats[customer];
      const monthlyData = sortedMonths.map(month =>
        customerData[month]?.assignedCases || 0
      );

      return {
        label: customer,
        data: monthlyData,
        borderColor: generateColor(index),
        backgroundColor: generateColor(index) + '20',
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        hidden: visibleCustomers[customer] === false,
      };
    });

    return {
      labels: formattedMonths,
      datasets,
      sortedCustomers,
    };
  };

  // Toggle customer visibility
  const toggleCustomer = (customer: string) => {
    setVisibleCustomers(prev => ({
      ...prev,
      [customer]: prev[customer] === false
    }));
  };

  // Chart options for customer trend
  const customerTrendOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      title: {
        display: true,
        text: 'Newly created cases per month',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Cases Created',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Month',
        },
      },
    },
  };

  // Get filtered chart data for customers
  const getFilteredCustomerChartData = (chartData: any) => {
    if (!chartData) return null;

    return {
      ...chartData,
      datasets: chartData.datasets.filter((dataset: any) =>
        visibleCustomers[dataset.label] !== false
      ),
    };
  };

  if (loading) return <LoadingSpinner title="Loading customer support data..." />;
  if (error) return <div>Error: {error}</div>;
  if (!data || !data.summaryId) {
    return (
      <NoDataPlaceholder
        title="No Customer Support Data Available"
        message={
          <>
            <p>To view customer-specific support metrics, ensure an integration is set up in the Settings page.</p>
            <p>Once integrated, data should appear here within 10 minutes.</p>
          </>
        }
      />
    );
  }

  // Get top 5 customers with highest open cases for the risk cards
  const topAtRiskCustomers = Object.entries(data.casesByAccount)
    .map(([account, details]) => ({
      account,
      ...details,
      riskScore: data?.customerRiskPercentages?.[account] || 0
    }))
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 5);

  const dataSnapshotRange = `${format(new Date(data.snapshotStartDate), 'do MMMM yyyy HH:mm')} 
    - ${format(new Date(data.summaryDate), 'do MMMM yyyy HH:mm')}`;

  return (
    <div className="dashboard-container">
      {/* Mobile fixed top instance selector */}
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            paddingTop: 40,
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 16px' }}>
            <div style={{
              fontSize: 20,
              fontWeight: 300,
              color: '#374151',
              marginBottom: 8,
              textAlign: 'center',
              fontFamily: 'Raleway, sans-serif'
            }}>
              Support Customers
            </div>
            {instances && instances.length > 0 && (
              <select
                value={selectedSupportCustomerIntegrationId || ''}
                onChange={(e) => {
                  setSelectedCustomer(null);
                  setSelectedSupportCustomerIntegrationId(Number(e.target.value))
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  fontSize: 14,
                  color: '#111827'
                }}
              >
                {instances.map((instance) => (
                  <option key={instance.customerIntegrationId} value={instance.customerIntegrationId}>
                    {instance.instanceName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
      {/* Spacer to offset fixed header height on mobile */}
      {isMobile && <div style={{ height: 70 }} />}

      {/* Desktop header - hidden on mobile */}
      {!isMobile && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid #dee2e6'
        }}>
          <h1 className="dashboard-title" style={{
            fontSize: '2rem',
            lineHeight: '1.2',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 300,
            margin: 0
          }}>
            {data?.instanceName ? `${data.instanceName} - Customers` : 'Support Customer Performance'}
          </h1>

                {/* Title and Customer Selector Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns:  '1fr',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        
        
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'center',
          justifySelf: 'center'
        }}>
          <button
            onClick={() => setIsDialogOpen(true)}
            style={{
              background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)',
              color: 'white',
              border: 'none',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '1.75rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              transform: 'scale(1)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.4)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(41, 203, 243, 0.3)';
              //e.currentTarget.style.background = 'linear-gradient(115deg, rgb(77, 182, 159) 25%, rgb(34, 34, 69) 162%)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              //e.currentTarget.style.background = 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)';
            }}
          >
            {selectedCustomer ? "Change Customer" : "Select a Customer"}
          </button>
        {selectedCustomer && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => {
            setSelectedCustomer(null);
            setAiSummary(null);
          }}>
            <button
              onClick={() => setSelectedCustomer(null)}
              aria-label="Clear selection"
              style={{
                background: 'red',
                border: '1px solid red',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: 'white',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'dark red';
                e.currentTarget.style.borderColor = 'dark red';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'red';
                e.currentTarget.style.borderColor = 'red';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              View All{/* <CancelRoundedIcon className="h-5 w-5" /> */}
            </button>
          </div>
        )}
        </div>
        
      </div>
          
          {instances.length > 0 && (
            <SupportInstanceSelector
              instances={instances}
              selectedCustomerIntegrationId={selectedSupportCustomerIntegrationId}
              onSelectInstance={(id) => 
                {
                  setSelectedCustomer(null); 
                  setSelectedSupportCustomerIntegrationId(id)
                }}
            />
          )}
        </div>
      )}
      


      {data?.summaryDate && (
        <p className="dashboard-subtitle" style={{ marginBottom: '2rem', color: '#666', fontSize: '0.9rem' }}>
          Data snapshot: {dataSnapshotRange}
        </p>
      )}

      {/* Customer Selection Dialog */}
      {
        isDialogOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '50%',
              height: '65%',
              padding: '24px',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              position: 'relative'
            }}>
              {/* Close Button - Positioned Absolutely */}
              <button
                onClick={() => setIsDialogOpen(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ddd',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  zIndex: 10,
                  transition: 'all 0.2s ease',
                  paddingTop: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e0e0e0';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                aria-label="Close dialog"
              >
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>&times;</span>
              </button>

              {/* Dialog Header */}
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-indigo-900">Select a Customer</h2>
                <p><i>Customers are ordered by risk level with the highest risk at the top.</i></p>
              </div>

              {/* Search Input */}
              <div style={{ marginBottom: '1rem', position: 'relative', marginLeft: '40px' }}>
                <input
                  type="text"
                  placeholder="Search for a customer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  style={{ fontSize: '22px', height: '40px', width: '500px', verticalAlign: 'middle' }}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', verticalAlign: 'middle' }}
                  >
                    <CancelIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Customer Cards Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                overflowY: 'auto',
                padding: '8px 40px 22px 40px'
              }}>
                {sortedCustomers.length > 0 ? (
                  sortedCustomers.map((customer) => (
                    
                      <MetricCard
                        title={customer.name}
                        value={`${customer.riskScore}%`}
                        description={`${customer.openCases} open cases`}
                        frontValueColor={getRiskColor(customer.riskScore, true)}
                        frontCardColor={getRiskColor(customer.riskScore, false)}
                        icon={
                          <div className="text-white">
                            {customer.riskScore > 70 ? '⚠️' : customer.riskScore > 40 ? '⚠' : '✓'}
                          </div>
                        }
                        onNoFlipClick={() => handleCustomerSelect(customer.name)}
                      />
                    
                  ))
                ) : (
                  <div className="col-span-full text-gray-500 py-8 text-center">No customers found</div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* All Customers View */}
      {
        !selectedCustomer && (
          <div>
            {/* At Risk Customers Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Most At Risk Customers</h2>
              {windowWidth < 768 ? (
                <div
                  style={{                    
                    marginBottom: '1rem',
                    marginLeft: '1rem'
                  }}
                >
                  <MobileMetricSlider
                    cards={topAtRiskCustomers.map((customer) => (
                      
                        <MetricCard
                          title={customer.account}
                          value={`${customer.riskScore}%`}
                          description={`${customer.openCases} open cases, ${formatTime(customer.avgResolutionTime)} avg resolution`}
                          frontValueColor={getRiskColor(customer.riskScore, true)}
                          frontCardColor={getRiskColor(customer.riskScore, false)}
                          icon={
                            <div className="text-white">
                              <WarningIcon className="h-5 w-5" />
                            </div>
                          }
                          onNoFlipClick={() => setSelectedCustomer(customer.account)}
                        />
                      
                    ))}
                  />
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  {topAtRiskCustomers.map((customer) => (
                    <div
                      key={customer.account}
                      onClick={() => setSelectedCustomer(customer.account)}
                      className="hover:shadow-lg transition-shadow duration-200"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.cursor = 'pointer';
                        // Set cursor for all children
                        const allElements = e.currentTarget.querySelectorAll('*');
                        allElements.forEach((el: any) => {
                          el.style.cursor = 'pointer';
                        });
                      }}
                    >
                      <MetricCard
                        title={customer.account}
                        value={`${customer.riskScore}%`}
                        description={`${customer.openCases} open cases, ${formatTime(customer.avgResolutionTime)} avg resolution`}
                        frontValueColor={getRiskColor(customer.riskScore, true)}
                        frontCardColor={getRiskColor(customer.riskScore, false)}
                        icon={
                          <div className="text-white">
                            <WarningIcon className="h-5 w-5" />
                          </div>
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Middle Row Charts */}
            <div className="mb-8" style={{ 
              display: 'grid', 
              gridTemplateColumns: shouldStack ? '1fr' : '1fr 1fr', 
              gap: '1rem', 
              marginBottom: '1rem' 
            }}>
              {/* Cases Assigned by Customer Over Time Chart */}
              <div className="dashboard-card" style={{ 
                display: 'grid', 
                gridTemplateColumns: shouldStack ? '1fr' : '3fr 1fr',
                gap: shouldStack ? '1rem' : '0',
                marginBottom: shouldStack ? '1rem' : '0'
              }}>
                <div style={{ 
                  height: shouldStack ? '320px' : '400px',
                  order: shouldStack ? 2 : 1
                }}>
                  {processCustomerTrendData() ? (
                    <Line
                      options={customerTrendOptions}
                      data={getFilteredCustomerChartData(processCustomerTrendData())}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No customer trend data available</p>
                    </div>
                  )}
                </div>
                {processCustomerTrendData() && (
                  <div style={{ order: shouldStack ? 1 : 2 }}>
                    <ProductLegend
                      products={processCustomerTrendData()?.sortedCustomers || []}
                      visibleProducts={visibleCustomers}
                      onToggleProduct={toggleCustomer}
                      generateColor={generateColor}
                      chartData={processCustomerTrendData()}
                      divisor={1}
                      valuePostfix=""
                      labelSumDecimalPlaces={0}
                      isMobile={shouldStack}
                      title="Customers"
                    />
                  </div>
                )}
              </div>

              {/* Overall CSAT Trend Chart */}
              <div className="dashboard-card">
                <div style={{ height: shouldStack ? '320px' : '400px' }}>
                  {processOverallCsatTrendData() ? (
                    <Line
                      data={processOverallCsatTrendData() as ChartData<"line">}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: 1,
                            ticks: {
                              callback: (value) => `${Number(value) * 100}%`
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          title: {
                            display: true,
                            text: 'Customer Satisfaction Trend',
                            font: {
                              size: 16,
                              weight: 'bold'
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) => {
                                const value = context.parsed.y;
                                return `CSAT: ${(value * 100).toFixed(1)}%`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No CSAT data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* CSAT Performance Extremes Card */}
            <div className="mb-8"
              style={{ marginBottom: shouldStack ? '5rem' : '0' }}
            >
              <div className="dashboard-card">
                {processCsatExtremesData() ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: shouldStack ? '1fr' : '1fr 1fr', 
                    gap: shouldStack ? '1rem' : '2rem', 
                    maxHeight: shouldStack ? 'none' : '400px', 
                    overflowY: shouldStack ? 'visible' : 'auto', 
                    
                  }}>
                    {/* Highest Scoring Customers Table */}
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#43a047' }}>
                        🏆 Highest Scoring Customer CSAT Per Month
                      </h4>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse', 
                        fontSize: shouldStack ? '12px' : '14px',
                        minWidth: shouldStack ? '300px' : 'auto'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f4ffed', borderBottom: '2px solid #43a047' }}>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'left', fontWeight: 'bold' }}>Month</th>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'left', fontWeight: 'bold' }}>Customer</th>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>CSAT</th>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>Resp.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processCsatExtremesData()!.map((monthData, index) => (
                            <tr key={`highest-${monthData.month}`} style={{
                              borderBottom: '1px solid #dee2e6',
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fff9'
                            }}>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px', fontWeight: '500', fontSize: shouldStack ? '11px' : 'inherit' }}>{shouldStack ? monthData.formattedMonth.substring(0, 6) : monthData.formattedMonth}</td>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px' }}>
                                {monthData.highest ? (
                                  <button
                                    onClick={() => setSelectedCustomer(monthData.highest!.customer)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#1976d2',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      padding: 0,
                                      fontSize: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#3949ab'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#1976d2'}
                                  >
                                    {monthData.highest.customer}
                                  </button>
                                ) : 'N/A'}
                              </td>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px', textAlign: 'center' }}>
                                {monthData.highest ? (
                                  <span style={{
                                    color: monthData.highest.score >= 0.8 ? '#43a047' :
                                      monthData.highest.score >= 0.6 ? '#ff9800' : '#e53935',
                                    fontWeight: '600'
                                  }}>
                                    {(monthData.highest.score * 100).toFixed(1)}%
                                  </span>
                                ) : 'N/A'}
                              </td>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px', textAlign: 'center', color: '#666' }}>
                                {monthData.highest?.responses || 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Lowest Scoring Customers Table */}
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#e53935' }}>
                        ⚠️ Lowest Scoring Customer CSAT Per Month
                      </h4>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse', 
                        fontSize: shouldStack ? '12px' : '14px',
                        minWidth: shouldStack ? '300px' : 'auto'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#ffebee', borderBottom: '2px solid #e53935' }}>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'left', fontWeight: 'bold' }}>Month</th>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'left', fontWeight: 'bold' }}>Customer</th>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>CSAT</th>
                            <th style={{ padding: shouldStack ? '8px 4px' : '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>Resp.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processCsatExtremesData()!.map((monthData, index) => (
                            <tr key={`lowest-${monthData.month}`} style={{
                              borderBottom: '1px solid #dee2e6',
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#fff5f5'
                            }}>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px', fontWeight: '500', fontSize: shouldStack ? '11px' : 'inherit' }}>{shouldStack ? monthData.formattedMonth.substring(0, 6) : monthData.formattedMonth}</td>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px' }}>
                                {monthData.lowest ? (
                                  <button
                                    onClick={() => setSelectedCustomer(monthData.lowest!.customer)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#1976d2',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      padding: 0,
                                      fontSize: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#3949ab'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#1976d2'}
                                  >
                                    {monthData.lowest.customer}
                                  </button>
                                ) : 'N/A'}
                              </td>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px', textAlign: 'center' }}>
                                {monthData.lowest ? (
                                  <span style={{
                                    color: monthData.lowest.score >= 0.8 ? '#43a047' :
                                      monthData.lowest.score >= 0.6 ? '#ff9800' : '#e53935',
                                    fontWeight: '600'
                                  }}>
                                    {(monthData.lowest.score * 100).toFixed(1)}%
                                  </span>
                                ) : 'N/A'}
                              </td>
                              <td style={{ padding: shouldStack ? '8px 4px' : '10px 8px', textAlign: 'center', color: '#666' }}>
                                {monthData.lowest?.responses || 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <p className="text-gray-500">No CSAT extremes data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Customer-specific or All Customers data */}
      {
        selectedCustomer && (
          // Selected Customer View with grid-based layout
          <div
            style={{ marginBottom: isMobile ? '5rem' : '0' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 className="text-xl font-semibold">{selectedCustomer} Support Metrics
                {isMobile && (
                  <button
                    onClick={() => setSelectedCustomer(null)}
                    style={{
                      background: 'none',
                      border: '2px solid #dc2626',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'inline-flex',
                      marginLeft: '1rem',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#dc2626',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                      e.currentTarget.style.color = 'white';
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#dc2626';
                    }}
                    aria-label="Deselect customer"
                  >
                    <Close style={{ fontSize: '18px' }} />
                  </button>
                )}
              </h2>
              
            </div>

            {/* Customer Metrics Grid - Matching SupportOverview */}
            {windowWidth < 768 ? (
              <div style={{ marginBottom: '1rem', marginLeft: '1rem', padding: '1rem' }}>
                <MobileMetricSlider
                  cards={[
                    //High Priority Open Cases
                    <MetricCard
                      title="Critical/High Priority Open"
                          frontCardColor={(() => {
                            const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                            if (customerHighPriority.count < 1) {
                              return metricCardColorGood;
                            } else {
                              return metricCardColorBad;
                            }
                          })()}
                          description={(() => {
                            const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                            return `${customerHighPriority.percent.toFixed(1)}% of ${customerData?.openCases || 0} open cases`;
                          })()}
                          descriptionColor={(() => {
                            const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                            if (customerHighPriority.count < 1) {
                              return metricCardValueColorGood;
                            } else {
                              return metricCardValueColorBad;
                            }
                          })()}
                          value={(() => {
                            const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                            return customerHighPriority.count;
                          })()}
                          frontValueColor={(() => {
                            const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                            if (customerHighPriority.count < 1) {
                              return metricCardValueColorGood;
                            } else {
                              return metricCardValueColorBad;
                            }
                          })()}
                          isNegative={(() => {
                            return true;
                          })()}
                          icon={
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              style={{ transition: 'stroke 0.2s ease' }}
                            >
                              <circle cx="12" cy="12" r="10" />
                              <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                              <line x1="12" y1="11" x2="12" y2="16" />
                            </svg>
                          }
                          canFlip={true}
                          backCardColor={getHighPriorityTrendColor('card')}
                          backTitle='Critical/High Priority Opened This Month'
                          backValue={getLatestMonthHighPriorityCases()}
                          backValueColor={getHighPriorityTrendColor('value')}
                          backDescription={getHighPriorityTrendDescription()}
                      backDescriptionColor={getHighPriorityTrendColor('value')}
                    />,

                    <MetricCard
                      title={`Customer Satisfaction (Snapshot)`}
                      key="customer-satisfaction"                    
                      value={(() => {
                  // Calculate customer-specific CSAT average from all monthly data
                  if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
                    const customerScores: number[] = [];
                    Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                      const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                      if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                        customerScores.push(customerData.averageNormalizedScore);
                      }
                    });

                    if (customerScores.length > 0) {
                      const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                      return formatCsatScore(average);
                    }
                  }
                  return 'N/A';
                })()}
                description={(() => {
                  // Calculate total responses across all months for customer
                  if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
                    let totalResponses = 0;
                    let monthsWithData = 0;

                    Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                      const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                      if (customerData && typeof customerData.totalResponses === 'number') {
                        totalResponses += customerData.totalResponses;
                        monthsWithData++;
                      }
                    });

                    if (monthsWithData > 0) {
                      return `${totalResponses} total responses`;
                    }
                  }
                  return 'No CSAT data recorded';
                })()}
                frontValueColor={(() => {
                  // Get averaged CSAT score for color calculation
                  if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
                    const customerScores: number[] = [];
                    Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                      const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                      if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                        customerScores.push(customerData.averageNormalizedScore);
                      }
                    });

                    if (customerScores.length > 0) {
                      const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                      return average < 0.6
                        ? (average < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                        : metricCardValueColorGood;
                    }
                  }
                  return metricCardValueColorNeutral;
                })()}
                descriptionColor={(() => {
                  // Get averaged CSAT score for description color
                  if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
                    const customerScores: number[] = [];
                    Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                      const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                      if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                        customerScores.push(customerData.averageNormalizedScore);
                      }
                    });

                    if (customerScores.length > 0) {
                      const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                      return average < 0.6
                        ? (average < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                        : metricCardValueColorGood;
                    }
                  }
                  return metricCardValueColorNeutral;
                })()}
                frontCardColor={(() => {
                  // Get averaged CSAT score for card color
                  if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
                    const customerScores: number[] = [];
                    Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                      const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                      if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                        customerScores.push(customerData.averageNormalizedScore);
                      }
                    });

                    if (customerScores.length > 0) {
                      const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                      return average < 0.6
                        ? (average < 0.4 ? metricCardColorBad : metricCardColorNeutral)
                        : metricCardColorGood;
                    }
                  }
                  return metricCardColorNeutral;
                })()}
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <circle cx="9" cy="9" r="0.5" fill="currentColor" />
                    <circle cx="15" cy="9" r="0.5" fill="currentColor" />
                  </svg>
                }
                canFlip={true}
                backCardColor={getCsatTrendColor('card')}
                backTitle={`Customer Satisfaction (Current Month)`}
                backValue={(() => {
                  // Get current month CSAT score for customer
                  if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
                    // Get all months with customer data and sort them
                    const customerMonths = Object.keys(data.csatMonthlyAverageByCustomer)
                      .filter(month => data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer])
                      .sort();

                    if (customerMonths.length > 0) {
                      // Get the latest month's score
                      const latestMonth = customerMonths[customerMonths.length - 1];
                      const latestScore = data.csatMonthlyAverageByCustomer[latestMonth][selectedCustomer]?.averageNormalizedScore;

                      if (typeof latestScore === 'number') {
                        return formatCsatScore(latestScore);
                      }
                    }
                  }
                  return 'N/A';
                })()}
                backValueColor={getCsatTrendColor('value')}
                backDescription={getCsatTrendDescription()}
                      backDescriptionColor={getCsatTrendColor('value')}
                    />,

                    // SLA Breaches
                    <MetricCard
                      title="SLA Breaches (Snapshot)"
                      value={calculateCustomerSlaBreaches().count}
                      description={`${calculateCustomerSlaBreaches().percent.toFixed(1)}% of ${customerData?.count || 0} total cases`}
                      isNegative={true}
                      icon={
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ transition: 'stroke 0.2s ease' }}
                        >
                          <circle cx="12" cy="12" r="10" />
                          <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                          <line x1="12" y1="11" x2="12" y2="16" />
                        </svg>
                      }
                      canFlip={true}
                      frontValueColor={calculateCustomerSlaBreaches().count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
                      descriptionColor={calculateCustomerSlaBreaches().count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
                      frontCardColor={calculateCustomerSlaBreaches().count > 0 ? metricCardColorBad : metricCardColorGood}
                      backCardColor={getSlaBreachesTrendColor('card')}
                      backTitle='SLA Breaches (Current Month)'
                      backValue={(() => {
                        // Get current month SLA breach data for customer
                        if (data?.monthlySlaBreachesByCustomer && selectedCustomer) {
                          const customerMonths = Object.keys(data.monthlySlaBreachesByCustomer)
                            .filter(month => data.monthlySlaBreachesByCustomer?.[month]?.[selectedCustomer])
                            .sort();

                          if (customerMonths.length > 0) {
                            const latestMonth = customerMonths[customerMonths.length - 1];
                            const latestData = data.monthlySlaBreachesByCustomer[latestMonth][selectedCustomer];
                            const breachCount = latestData?.breachCount || 0;
                            //const breachRate = latestData?.breachRatePercentage || 0;
                            return `${breachCount}`;
                          }
                        }
                        return '0';
                      })()}
                      backValueColor={getSlaBreachesTrendColor('value')}
                      backDescription={getSlaBreachesTrendDescription()}
                      backDescriptionColor={getSlaBreachesTrendColor('value')}
                    />,

                    // Open Cases
                    <MetricCard
                      title="Still Open Cases (Snapshot)"
                      frontCardColor={((customerData?.openCases || 0) / (customerData?.count || 1) * 100) > 20 ? metricCardColorBad : metricCardColorGood}
                      value={customerData?.openCases || 0}
                      frontValueColor={((customerData?.openCases || 0) / (customerData?.count || 1) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
                      description={`${(((customerData?.openCases || 0) / (customerData?.count || 1)) * 100).toFixed(1)}% of ${customerData?.count || 0} total cases`}
                      descriptionColor={((customerData?.openCases || 0) / (customerData?.count || 1) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
                      icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14,2 14,8 20,8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10,9 9,9 8,9" />
                        </svg>
                      }
                      canFlip={true}
                      backCardColor={getOpenedCasesTrendColor('card')}
                      backTitle='Newly Opened Cases (Current Month)'
                      backValue={getCustomerMonthlyOpenedCases()}
                      backValueColor={getOpenedCasesTrendColor('value')}
                      backDescription={getOpenedCasesTrendDescription()}
                      backDescriptionColor={getOpenedCasesTrendColor('value')}
                    />,

                    // First Response Time
                    <MetricCard
                      title="First Response Time (Snapshot)"
                      description="Average Time to First Response"
                      descriptionColor={(() => {
                        const customerAvgFirstResponse = customerData?.avgFirstResponseTime || 0;
                        if (customerAvgFirstResponse < 120) {
                          return metricCardValueColorGood;
                        } else {
                          return metricCardValueColorBad;
                        }
                      })()}
                      value={formatTime(customerData?.avgFirstResponseTime || 0)}
                      frontValueColor={(() => {
                        const customerAvgFirstResponse = customerData?.avgFirstResponseTime || 0;
                        if (customerAvgFirstResponse < 120) {
                          return metricCardValueColorGood;
                        } else {
                          return metricCardValueColorBad;
                        }
                      })()}
                      frontCardColor={(() => {
                        const customerAvgFirstResponse = customerData?.avgFirstResponseTime || 0;
                        if (customerAvgFirstResponse < 120) {
                          return metricCardColorGood;
                        } else {
                          return metricCardColorBad;
                        }
                      })()}
                      icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12,6 12,12 16,14" />
                          <line x1="12" y1="20" x2="12" y2="22" />
                          <line x1="20" y1="12" x2="22" y2="12" />
                          <line x1="2" y1="12" x2="4" y2="12" />
                          <circle cx="16" cy="0" r="0.5" />
                          <circle cx="8" cy="0" r="0.5" />
                        </svg>
                      }
                      canFlip={true}
                      backCardColor={getFirstResponseTimeTrendColor('card')}
                      backTitle='First Response Time (Current Month)'
                      backValue={(() => {
                        // Get current month first response time for customer
                        if (data?.accountMonthlyTrendStats && selectedCustomer) {
                          const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
                          if (customerMonthlyData) {
                            const months = Object.keys(customerMonthlyData).sort();
                            if (months.length > 0) {
                              const latestMonth = months[months.length - 1];
                              const latestData = customerMonthlyData[latestMonth];
                              const time = latestData?.avgFirstResponseTime || 0;
                              return formatTime(time);
                            }
                          }
                        }
                        return 'N/A';
                      })()}
                      backValueColor={getFirstResponseTimeTrendColor('value')}
                      backDescription={getFirstResponseTimeTrendDescription()}
                      backDescriptionColor={getFirstResponseTimeTrendColor('value')}
                    />,

                    // Resolution Time
                    <MetricCard
                      title="Resolution Time (Snapshot)"
                      frontCardColor={(() => {
                        const customerAvgResolution = customerData?.avgResolutionTime || 0;
                        if (customerAvgResolution < 300) {
                          return metricCardColorGood;
                        } else {
                          return metricCardColorBad;
                        }
                      })()}
                      value={formatTime(customerData?.avgResolutionTime || 0)}
                      frontValueColor={(() => {
                        const customerAvgResolution = customerData?.avgResolutionTime || 0;
                        if (customerAvgResolution < 300) {
                          return metricCardValueColorGood;
                        } else {
                          return metricCardValueColorBad;
                        }
                      })()}
                      description="Time to close cases"
                      descriptionColor={(() => {
                        const customerAvgResolution = customerData?.avgResolutionTime || 0;
                        if (customerAvgResolution < 300) {
                          return metricCardValueColorGood;
                        } else {
                          return metricCardValueColorBad;
                        }
                      })()}
                      icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12,6 12,12 16,14" />
                        </svg>
                      }
                      canFlip={true}
                      backCardColor={getResolutionTimeTrendColor('card')}
                      backTitle='Resolution Time (Current Month)'
                      backValue={(() => {
                        // Get current month resolution time for customer
                        if (data?.accountMonthlyTrendStats && selectedCustomer) {
                          const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
                          if (customerMonthlyData) {
                            const months = Object.keys(customerMonthlyData).sort();
                            if (months.length > 0) {
                              const latestMonth = months[months.length - 1];
                              const latestData = customerMonthlyData[latestMonth];
                              const time = latestData?.avgResolutionTime || 0;
                              return formatTime(time);
                            }
                          }
                        }
                        return 'N/A';
                      })()}
                      backValueColor={getResolutionTimeTrendColor('value')}
                      backDescription={getResolutionTimeTrendDescription()}
                      backDescriptionColor={getResolutionTimeTrendColor('value')}
                    />
                  ]}
                />
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
              {/* High Priority Open Cases */}
              <MetricCard
                title="Critical/High Priority Open"
                    frontCardColor={(() => {
                      const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                      if (customerHighPriority.count < 1) {
                        return metricCardColorGood;
                      } else {
                        return metricCardColorBad;
                      }
                    })()}
                    description={(() => {
                      const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                      return `${customerHighPriority.percent.toFixed(1)}% of ${customerData?.openCases || 0} open cases`;
                    })()}
                    descriptionColor={(() => {
                      const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                      if (customerHighPriority.count < 1) {
                        return metricCardValueColorGood;
                      } else {
                        return metricCardValueColorBad;
                      }
                    })()}
                    value={(() => {
                      const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                      return customerHighPriority.count;
                    })()}
                    frontValueColor={(() => {
                      const customerHighPriority = calculateCustomerHighPriorityOpenCases();
                      if (customerHighPriority.count < 1) {
                        return metricCardValueColorGood;
                      } else {
                        return metricCardValueColorBad;
                      }
                    })()}
                    isNegative={(() => {
                      return true;
                    })()}
                    icon={
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ transition: 'stroke 0.2s ease' }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                        <line x1="12" y1="11" x2="12" y2="16" />
                      </svg>
                    }
                    canFlip={true}
                    backCardColor={getHighPriorityTrendColor('card')}
                    backTitle='Critical/High Priority Opened This Month'
                    backValue={getLatestMonthHighPriorityCases()}
                    backValueColor={getHighPriorityTrendColor('value')}
                    backDescription={getHighPriorityTrendDescription()}
                    backDescriptionColor={getHighPriorityTrendColor('value')}
              />

              <MetricCard
                title={`Customer Satisfaction (Snapshot)`}
                key="customer-satisfaction"                    
                value={(() => {
            // Calculate customer-specific CSAT average from all monthly data
            if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
              const customerScores: number[] = [];
              Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                  customerScores.push(customerData.averageNormalizedScore);
                }
              });

              if (customerScores.length > 0) {
                const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                return formatCsatScore(average);
              }
            }
            return 'N/A';
          })()}
          description={(() => {
            // Calculate total responses across all months for customer
            if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
              let totalResponses = 0;
              let monthsWithData = 0;

              Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                if (customerData && typeof customerData.totalResponses === 'number') {
                  totalResponses += customerData.totalResponses;
                  monthsWithData++;
                }
              });

              if (monthsWithData > 0) {
                return `${totalResponses} total responses`;
              }
            }
            return 'No CSAT data recorded';
          })()}
          frontValueColor={(() => {
            // Get averaged CSAT score for color calculation
            if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
              const customerScores: number[] = [];
              Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                  customerScores.push(customerData.averageNormalizedScore);
                }
              });

              if (customerScores.length > 0) {
                const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                return average < 0.6
                  ? (average < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                  : metricCardValueColorGood;
              }
            }
            return metricCardValueColorNeutral;
          })()}
          descriptionColor={(() => {
            // Get averaged CSAT score for description color
            if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
              const customerScores: number[] = [];
              Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                  customerScores.push(customerData.averageNormalizedScore);
                }
              });

              if (customerScores.length > 0) {
                const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                return average < 0.6
                  ? (average < 0.4 ? metricCardValueColorBad : metricCardValueColorNeutral)
                  : metricCardValueColorGood;
              }
            }
            return metricCardValueColorNeutral;
          })()}
          frontCardColor={(() => {
            // Get averaged CSAT score for card color
            if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
              const customerScores: number[] = [];
              Object.keys(data.csatMonthlyAverageByCustomer).forEach(month => {
                const customerData = data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer];
                if (customerData && typeof customerData.averageNormalizedScore === 'number') {
                  customerScores.push(customerData.averageNormalizedScore);
                }
              });

              if (customerScores.length > 0) {
                const average = customerScores.reduce((sum, score) => sum + score, 0) / customerScores.length;
                return average < 0.6
                  ? (average < 0.4 ? metricCardColorBad : metricCardColorNeutral)
                  : metricCardColorGood;
              }
            }
            return metricCardColorNeutral;
          })()}
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <circle cx="9" cy="9" r="0.5" fill="currentColor" />
              <circle cx="15" cy="9" r="0.5" fill="currentColor" />
            </svg>
          }
          canFlip={true}
          backCardColor={getCsatTrendColor('card')}
          backTitle={`Customer Satisfaction (Current Month)`}
          backValue={(() => {
            // Get current month CSAT score for customer
            if (data?.csatMonthlyAverageByCustomer && selectedCustomer) {
              // Get all months with customer data and sort them
              const customerMonths = Object.keys(data.csatMonthlyAverageByCustomer)
                .filter(month => data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer])
                .sort();

              if (customerMonths.length > 0) {
                // Get the latest month's score
                const latestMonth = customerMonths[customerMonths.length - 1];
                const latestScore = data.csatMonthlyAverageByCustomer[latestMonth][selectedCustomer]?.averageNormalizedScore;

                if (typeof latestScore === 'number') {
                  return formatCsatScore(latestScore);
                }
              }
            }
            return 'N/A';
          })()}
          backValueColor={getCsatTrendColor('value')}
          backDescription={getCsatTrendDescription()}
                backDescriptionColor={getCsatTrendColor('value')}
              />

              {/* SLA Breaches */}
              <MetricCard
                title="SLA Breaches (Snapshot)"
                value={calculateCustomerSlaBreaches().count}
                description={`${calculateCustomerSlaBreaches().percent.toFixed(1)}% of ${customerData?.count || 0} total cases`}
                isNegative={true}
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ transition: 'stroke 0.2s ease' }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
                    <line x1="12" y1="11" x2="12" y2="16" />
                  </svg>
                }
                canFlip={true}
                frontValueColor={calculateCustomerSlaBreaches().count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
                descriptionColor={calculateCustomerSlaBreaches().count > 0 ? metricCardValueColorBad : metricCardValueColorGood}
                frontCardColor={calculateCustomerSlaBreaches().count > 0 ? metricCardColorBad : metricCardColorGood}
                backCardColor={getSlaBreachesTrendColor('card')}
                backTitle='SLA Breaches (Current Month)'
                backValue={(() => {
                  // Get current month SLA breach data for customer
                  if (data?.monthlySlaBreachesByCustomer && selectedCustomer) {
                    const customerMonths = Object.keys(data.monthlySlaBreachesByCustomer)
                      .filter(month => data.monthlySlaBreachesByCustomer?.[month]?.[selectedCustomer])
                      .sort();

                    if (customerMonths.length > 0) {
                      const latestMonth = customerMonths[customerMonths.length - 1];
                      const latestData = data.monthlySlaBreachesByCustomer[latestMonth][selectedCustomer];
                      const breachCount = latestData?.breachCount || 0;
                      //const breachRate = latestData?.breachRatePercentage || 0;
                      return `${breachCount}`;
                    }
                  }
                  return '0';
                })()}
                backValueColor={getSlaBreachesTrendColor('value')}
                backDescription={getSlaBreachesTrendDescription()}
                backDescriptionColor={getSlaBreachesTrendColor('value')}
              />

              {/* Open Cases */}
              <MetricCard
                title="Still Open Cases (Snapshot)"
                frontCardColor={((customerData?.openCases || 0) / (customerData?.count || 1) * 100) > 20 ? metricCardColorBad : metricCardColorGood}
                value={customerData?.openCases || 0}
                frontValueColor={((customerData?.openCases || 0) / (customerData?.count || 1) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
                description={`${(((customerData?.openCases || 0) / (customerData?.count || 1)) * 100).toFixed(1)}% of ${customerData?.count || 0} total cases`}
                descriptionColor={((customerData?.openCases || 0) / (customerData?.count || 1) * 100) > 20 ? metricCardValueColorBad : metricCardValueColorGood}
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                }
                canFlip={true}
                backCardColor={getOpenedCasesTrendColor('card')}
                backTitle='Newly Opened Cases (Current Month)'
                backValue={getCustomerMonthlyOpenedCases()}
                backValueColor={getOpenedCasesTrendColor('value')}
                backDescription={getOpenedCasesTrendDescription()}
                backDescriptionColor={getOpenedCasesTrendColor('value')}
              />

              {/* First Response Time */}
              <MetricCard
                title="First Response Time (Snapshot)"
                description="Average Time to First Response"
                descriptionColor={(() => {
                  const customerAvgFirstResponse = customerData?.avgFirstResponseTime || 0;
                  if (customerAvgFirstResponse < 120) {
                    return metricCardValueColorGood;
                  } else {
                    return metricCardValueColorBad;
                  }
                })()}
                value={formatTime(customerData?.avgFirstResponseTime || 0)}
                frontValueColor={(() => {
                  const customerAvgFirstResponse = customerData?.avgFirstResponseTime || 0;
                  if (customerAvgFirstResponse < 120) {
                    return metricCardValueColorGood;
                  } else {
                    return metricCardValueColorBad;
                  }
                })()}
                frontCardColor={(() => {
                  const customerAvgFirstResponse = customerData?.avgFirstResponseTime || 0;
                  if (customerAvgFirstResponse < 120) {
                    return metricCardColorGood;
                  } else {
                    return metricCardColorBad;
                  }
                })()}
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                    <line x1="12" y1="20" x2="12" y2="22" />
                    <line x1="20" y1="12" x2="22" y2="12" />
                    <line x1="2" y1="12" x2="4" y2="12" />
                    <circle cx="16" cy="0" r="0.5" />
                    <circle cx="8" cy="0" r="0.5" />
                  </svg>
                }
                canFlip={true}
                backCardColor={getFirstResponseTimeTrendColor('card')}
                backTitle='First Response Time (Current Month)'
                backValue={(() => {
                  // Get current month first response time for customer
                  if (data?.accountMonthlyTrendStats && selectedCustomer) {
                    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
                    if (customerMonthlyData) {
                      const months = Object.keys(customerMonthlyData).sort();
                      if (months.length > 0) {
                        const latestMonth = months[months.length - 1];
                        const latestData = customerMonthlyData[latestMonth];
                        const time = latestData?.avgFirstResponseTime || 0;
                        return formatTime(time);
                      }
                    }
                  }
                  return 'N/A';
                })()}
                backValueColor={getFirstResponseTimeTrendColor('value')}
                backDescription={getFirstResponseTimeTrendDescription()}
                backDescriptionColor={getFirstResponseTimeTrendColor('value')}
              />

              {/* Resolution Time */}
              <MetricCard
                title="Resolution Time (Snapshot)"
                frontCardColor={(() => {
                  const customerAvgResolution = customerData?.avgResolutionTime || 0;
                  if (customerAvgResolution < 300) {
                    return metricCardColorGood;
                  } else {
                    return metricCardColorBad;
                  }
                })()}
                value={formatTime(customerData?.avgResolutionTime || 0)}
                frontValueColor={(() => {
                  const customerAvgResolution = customerData?.avgResolutionTime || 0;
                  if (customerAvgResolution < 300) {
                    return metricCardValueColorGood;
                  } else {
                    return metricCardValueColorBad;
                  }
                })()}
                description="Time to close cases"
                descriptionColor={(() => {
                  const customerAvgResolution = customerData?.avgResolutionTime || 0;
                  if (customerAvgResolution < 300) {
                    return metricCardValueColorGood;
                  } else {
                    return metricCardValueColorBad;
                  }
                })()}
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                  </svg>
                }
                canFlip={true}
                backCardColor={getResolutionTimeTrendColor('card')}
                backTitle='Resolution Time (Current Month)'
                backValue={(() => {
                  // Get current month resolution time for customer
                  if (data?.accountMonthlyTrendStats && selectedCustomer) {
                    const customerMonthlyData = data.accountMonthlyTrendStats[selectedCustomer];
                    if (customerMonthlyData) {
                      const months = Object.keys(customerMonthlyData).sort();
                      if (months.length > 0) {
                        const latestMonth = months[months.length - 1];
                        const latestData = customerMonthlyData[latestMonth];
                        const time = latestData?.avgResolutionTime || 0;
                        return formatTime(time);
                      }
                    }
                  }
                  return 'N/A';
                })()}
                backValueColor={getResolutionTimeTrendColor('value')}
                backDescription={getResolutionTimeTrendDescription()}
                backDescriptionColor={getResolutionTimeTrendColor('value')}
              />
            </div>
            )}

            {/* Middle Row: Line Charts in 2x2 Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: windowWidth < 768 ? '1fr' : '1fr 1fr', 
              gap: '1rem', 
              marginBottom: '1rem' 
            }}>
              <div className="dashboard-card">
                {/* <h3 className="text-lg font-medium mb-2">CSAT Score Over Time</h3> */}
                {(() => {
                  // Check if we have customer-specific data for this customer
                  const hasCustomerSpecificData = data?.csatMonthlyAverageByCustomer && selectedCustomer &&
                    Object.keys(data.csatMonthlyAverageByCustomer).some(month =>
                      data.csatMonthlyAverageByCustomer?.[month]?.[selectedCustomer]
                    );

                  if (hasCustomerSpecificData) {
                    return <p className="text-sm text-green-600 mb-3 italic">Showing {selectedCustomer}-specific CSAT trends</p>;
                  } else if (csatData) {
                    return <p className="text-sm text-gray-600 mb-3 italic">No customer-specific CSAT data available</p>;
                  } else {
                    return <p className="text-sm text-gray-600 mb-3 italic"></p>;
                  }
                })()}

                <div style={{ height: '250px' }}>
                  {csatData ? (
                    <Line
                      data={csatData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: 1,
                            ticks: {
                              callback: (value) => `${Number(value) * 100}%`
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          title: {
                            display: true,
                            text: `CSAT Score Over Time`,
                            font: {
                              size: 16,
                              weight: 'bold',
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) => {
                                const value = context.parsed.y;
                                const isCustomerSpecific = csatData.datasets[0].label?.includes(selectedCustomer || '');
                                return `${isCustomerSpecific ? selectedCustomer + ' ' : 'Overall '}CSAT: ${(value * 100).toFixed(1)}%`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div style={{ height: '250px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <p className="text-gray-500">Sorry, no customer satisfaction data available in the snapshot</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-card">
                {/* <h3 className="text-lg font-medium mb-2">Opened vs Closed Cases</h3> */}
                <div style={{ height: '250px' }}>
                  {caseRatesData ? (
                    <Line
                      data={caseRatesData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              precision: 0
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          title: {
                            display: true,
                            text: `Cumulative Opened vs Closed Cases`,
                            font: {
                              size: 16,
                              weight: 'bold',
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No case data available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-card">
                {/* <h3 className="text-lg font-medium mb-2">Average Resolution Time</h3> */}
                <div style={{ height: '250px' }}>
                  {resolutionTimeData ? (
                    <Line
                      data={resolutionTimeData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Hours'
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            position: 'top',
                            title: {
                              display: true,
                              text: `Average Resolution Time`,
                              font: {
                                size: 16,
                                weight: 'bold',
                              }
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) => {
                                const value = context.parsed.y;
                                return `Avg Resolution Time: ${value.toFixed(1)} hours`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No resolution time data available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-card">
                {/* <h3 className="text-lg font-medium mb-2">Average First Response Time</h3> */}
                <div style={{ height: '250px' }}>
                  {responseTimeData ? (
                    <Line
                      data={responseTimeData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Hours'
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          title: {
                            display: true,
                            text: `Average First Response Time`,
                            font: {
                              size: 16,
                              weight: 'bold',
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: (context: any) => {
                                const value = context.parsed.y;
                                return `Avg First Response Time: ${value.toFixed(1)} hours`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No priority data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Backlog Aging Profile Chart */}
              <div className="mb-4">
                <div className="dashboard-card">
                  <div style={{ width: '100%', height: windowWidth < 768 ? '250px' : '300px' }}>
                    {processCustomerBacklogAgingData(selectedCustomer) ? (
                      <Bar
                        data={processCustomerBacklogAgingData(selectedCustomer)!}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: 'y', // Horizontal bar chart
                          scales: {
                            x: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Number of Open Cases'
                              }
                            },
                            y: {
                              title: {
                                display: true,
                                text: 'Time Open'
                              }
                            }
                          },
                          plugins: {
                            legend: {
                              display: false
                            },
                            title: {
                              display: true,
                              text: `Backlog Aging Profile`,
                              font: {
                                size: 16,
                                weight: 'bold',
                              },
                            },
                            tooltip: {
                              callbacks: {
                                label: function (context) {
                                  return `${context.parsed.x} cases`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    ) : (
                      <div style={{ height: '250px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <p className="text-gray-500">No open ticket aging profile data available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Priority Status Breakdown Chart for Customer */}
              <div className="mb-4">
                <div className="dashboard-card">
                  <div style={{ width: '100%', height: windowWidth < 768 ? '250px' : '300px' }}>
                    {processCustomerPriorityStatusData(selectedCustomer) ? (
                      <Bar
                        data={processCustomerPriorityStatusData(selectedCustomer)!}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            x: {
                              stacked: true,
                              title: {
                                display: true,
                                text: 'Priority'
                              }
                            },
                            y: {
                              stacked: true,
                              title: {
                                display: true,
                                text: 'Number of Cases'
                              },
                              beginAtZero: true
                            }
                          },
                          plugins: {
                            tooltip: {
                              mode: 'index',
                              intersect: false
                            },
                            legend: {
                              position: 'top',
                              labels: {
                                boxWidth: 12,
                                usePointStyle: true
                              }
                            },
                            title: {
                              display: true,
                              text: `Cases by Priority & Status`,
                              font: {
                                size: 16,
                                weight: 'bold',
                              },
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-gray-500">No priority breakdown data available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* AI Analysis Panel - Takes 1 column */}
              {/* <div className="dashboard-card">
              <h3 className="text-lg font-medium mb-2">AI Analysis</h3>
              <div className="h-full">
                <StaticAiPanel
                  insight={data.aiAccountSummary?.AISummary || `No AI analysis available for ${selectedCustomer}.`}
                  contextType="support-overview"
                  entityName={selectedCustomer}
                  entityId={selectedCustomer}
                />
              </div>
            </div> */}
            </div>
          </div>
        )
      }

      {/* AI Panel */}
      {
        data && (() => {
          const canGenerateAiSummary = (aiGeneratedDate: string | undefined): boolean => {
            return aiGeneratedDate && data?.summaryDate ? new Date(aiGeneratedDate) < new Date(data.summaryDate) : true;
          };

          return (
            <SlidingAiPanel
              insight={aiSummary?.summary || ''}
              summaryCreatedOn={aiSummary?.createdOn}
              canGenerateNewSummary={canGenerateAiSummary(aiSummary?.createdOn)}
              contextType={selectedCustomer ? "support-customer" : "support-all-customers"}
              entityName={selectedCustomer ? selectedCustomer : 'the aggregated customer base'}
              entityId={selectedCustomer ? selectedCustomer : data.summaryId?.toString()}
              projectId={undefined}
              summaryId={data.summaryId?.toString()}
              initialOpen={false}
              shouldCloseOnOutisdeClick={false}
              buttonTitle={selectedCustomer ? "Exec Customer Insights" : "Exec Aggregated Insights"}
              onStreamedContentChange={updateAiSummary}
              generateInitialAi={selectedCustomer ? fetchCustomerSummary : undefined}
            />
          );
        })()
      }

      {/* Mobile drawer implementation */}
      {isMobile && (
        <>
          {/* Mobile drawer overlay */}
          {mobileDrawerOpen && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999
              }}
              onClick={() => setMobileDrawerOpen(false)}
            />
          )}

          {/* Mobile drawer panel */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: mobileDrawerOpen ? 0 : '-100%',
              width: '75%',
              height: '100vh',
              backgroundColor: 'white',
              zIndex: 1000,
              transition: 'left 0.3s ease-in-out',
              overflowY: 'auto',
              padding: '50px 20px 20px 20px',
              boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setMobileDrawerOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>

            {/* Drawer header */}
            <div style={{ marginBottom: '20px', paddingTop: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>Select a Customer</h2>
              <p style={{ color: '#6b7280', fontSize: '14px' }}>Customers are ordered by risk level with the highest risk at the top.</p>
            </div>

            {/* Search input */}
            <div style={{ marginBottom: '20px', position: 'relative', marginRight: '5rem' }}>
              <input
                type="text"
                placeholder="Search for a customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '16px'
                }}
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  <CancelIcon style={{ fontSize: '20px' }} />
                </button>
              )}
            </div>

            {/* Customer list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0rem' }}>
              {sortedCustomers.length > 0 ? (
                sortedCustomers.map((customer) => (                  
                   
                    <MetricCard
                    key={customer.name}
                      title={customer.name}
                      value={`${customer.riskScore}%`}
                      description={`${customer.openCases} open cases`}
                      frontValueColor={getRiskColor(customer.riskScore, true)}
                      frontCardColor={getRiskColor(customer.riskScore, false)}
                      icon={
                        <div className="text-white">
                          {customer.riskScore > 70 ? '⚠️' : customer.riskScore > 40 ? '⚠' : '✓'}
                        </div>
                      }
                      onNoFlipClick={() => {
                        handleCustomerSelect(customer.name);
                        setMobileDrawerOpen(false);
                      }}
                    />                 
                    
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
                  No customers found
                </div>
              )}
            </div>
          </div>

          {/* Mobile drawer handle */}
          <div 
            style={{
              position: 'fixed',
              left: 0,
              top: '50vh',
              transform: 'translateY(-50%)',
              background: '#2563eb',
              color: 'white',
              padding: '12px 4px',
              borderRadius: '0 8px 8px 0',
              cursor: 'pointer',
              zIndex: 998,
              display: mobileDrawerOpen ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '2px 0 8px rgba(0, 0, 0, 0.2)',
              transition: 'background-color 0.2s'
            }}
            onClick={() => setMobileDrawerOpen(true)}
            onTouchStart={(e) => {
              e.currentTarget.style.background = '#1d4ed8';
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.background = '#2563eb';
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ChevronRight style={{ fontSize: '24px', color: 'white' }} />
            </div>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              whiteSpace: 'nowrap'
            }}>{'Choose Customer'}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default SupportCustomers;
